import { CategoricalPalette } from './types';

export type AgbChangeBin =
  | 'large_loss'
  | 'loss'
  | 'stable'
  | 'gain'
  | 'large_gain';

// AGB change product — single source of truth for the 5-bin classification.
// The precompute (scripts/generate-agb-change-data.py) emits a categorical
// PNG using these exact bin edges and the hex palette below; if either
// drifts from its mirror in the script, the on-screen legend will lie about
// what the map shows. The structural test
// src/__tests__/agbChangeBins.test.ts pins the shape so drift fails CI
// instead of an audience.
//
// Bin definition (4 ascending cuts → 5 closed-on-the-right bins):
//   value < -20            → large_loss
//   -20 <= value < -5      → loss
//   -5  <= value < 5       → stable    (also covers exact-zero pixels)
//   5   <= value < 20      → gain
//   value >= 20            → large_gain
//
// Units: Mg/ha. The change is computed as the smoothed-endpoint difference
// `mean(2021..2025) - mean(2000..2004)` per AGB 100m pixel — five years of
// averaging on each end suppresses single-year AGB noise without obscuring
// real trends.
export const AGB_CHANGE_BIN_EDGES = [-20, -5, 5, 20] as const;

// Diverging palette tuned for the figma dark theme. Red → gray → green
// reads as "loss → stable → gain" without colour-vision ambiguity (the
// neutral middle is gray rather than yellow so the "stable" cells recede
// visually instead of competing with the active classes).
//
// Hex values MUST stay byte-identical to AGB_CHANGE_PALETTE_HEX in
// scripts/generate-agb-change-data.py. If you change one, change both AND
// re-run the precompute AND bump AGB_CHANGE_VINTAGE in apiConfig.ts.
export const AGB_CHANGE_PALETTE: CategoricalPalette<AgbChangeBin> = {
  colors: {
    large_loss: '#D73027',
    loss:       '#FC8D59',
    stable:     '#999999',
    gain:       '#91CF60',
    large_gain: '#1A9850',
  },
  labels: {
    large_loss: 'Large loss',
    loss:       'Loss',
    stable:     'Stable',
    gain:       'Gain',
    large_gain: 'Large gain',
  },
  // Render top → bottom in the legend with gain first — the redesign's
  // headline question is "how well is carbon being maintained?", so a
  // viewer scanning the legend sees the positive cells first.
  order: ['large_gain', 'gain', 'stable', 'loss', 'large_loss'],
};

// Human-readable Mg/ha range for each bin, used by the legend caption row
// and the per-ownership crosstab tooltip.
export const agbChangeBinRangeLabel = (bin: AgbChangeBin): string => {
  const [b0, b1, b2, b3] = AGB_CHANGE_BIN_EDGES;
  switch (bin) {
    case 'large_loss': return `< ${b0} Mg/ha`;
    case 'loss':       return `${b0} to ${b1} Mg/ha`;
    case 'stable':     return `${b1} to +${b2} Mg/ha`;
    case 'gain':       return `+${b2} to +${b3} Mg/ha`;
    case 'large_gain': return `> +${b3} Mg/ha`;
  }
};

// Convenience groupings for the per-ownership crosstab card. "Loss" and
// "gain" rollups bucket the two bins on each side of stable; the headline
// "% loss / stable / gain" share is computed from these.
export const AGB_CHANGE_LOSS_BINS: AgbChangeBin[] = ['large_loss', 'loss'];
export const AGB_CHANGE_GAIN_BINS: AgbChangeBin[] = ['gain', 'large_gain'];

// Endpoint windows used by the precompute. Surfaced here so the card's
// provenance footer ("mean(2021–2025) − mean(2000–2004)") and the
// precompute script reference the same constants.
export const AGB_CHANGE_BASELINE_YEARS = [2000, 2001, 2002, 2003, 2004] as const;
export const AGB_CHANGE_LATEST_YEARS = [2021, 2022, 2023, 2024, 2025] as const;
